import { Collapse, Grid, IconButton, makeStyles, Paper, Typography } from '@material-ui/core';
import { CameraAlt, ExpandLess, ExpandMore, OpenInNew } from '@material-ui/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionTooltip from 'src/components/ActionTooltip';
import { useCurrency } from 'src/hooks/useCurrency';
import { MonetaryAmount, RecommendedAction, ReasonDto, SourceType } from 'src/modules/generated/api';
import { getRecommendationReasonText } from 'src/modules/labels';

export type RecommendationCardProps = {
  imageThumbnailLink?: string;
  model?: string;
  basePrice?: MonetaryAmount;
  recommendedAction?: RecommendedAction;
  reason?: ReasonDto;
  source?: SourceType;
  carId?: string;
  variant?: 'card' | 'row';
  onOpen?: () => void;
  /** card variant only: show explanation below the badge (default false) */
  showExplanation?: boolean;
};

const useSharedStyles = makeStyles((theme) => ({
  actionBadge: {
    display: 'inline-block',
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.intelligence.light,
    color: theme.palette.common.white,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
}));

type SharedData = {
  actionLabel: string | undefined;
  formattedPrice: string | undefined;
  infoText: string;
  altText: string;
  detailHref?: string;
  onOpen?: () => void;
};

// --- Card styling ---

const useCardStyles = makeStyles((theme) => ({
  cardButton: {
    display: 'block',
    width: '100%',
    height: '100%',
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover $root': {
      boxShadow: theme.shadows[4],
    },
  },
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  imageWrapper: {
    aspectRatio: '4/3',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flex: 1,
    marginTop: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  model: {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  infoBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.intelligence.light}`,
  },
}));

const CardStyle = ({
  imageThumbnailLink,
  altText,
  model,
  formattedPrice,
  actionLabel,
  infoText,
  onOpen,
  showExplanation,
}: SharedData & Pick<RecommendationCardProps, 'imageThumbnailLink' | 'model' | 'showExplanation'>) => {
  const classes = useCardStyles();
  const shared = useSharedStyles();

  const paper = (
    <Paper className={classes.root} elevation={2}>
      <div className={classes.imageWrapper}>
        {imageThumbnailLink ? (
          <img src={imageThumbnailLink} alt={altText} className={classes.thumbnail} />
        ) : (
          <span aria-label={altText}>
            <CameraAlt fontSize="large" color="disabled" />
          </span>
        )}
      </div>
      <div className={classes.content}>
        {model && (
          <Typography variant="subtitle1" className={classes.model} title={model}>
            {model}
          </Typography>
        )}
        {formattedPrice && <Typography variant="body2">{formattedPrice}</Typography>}
        {actionLabel && <span className={shared.actionBadge}>{actionLabel}</span>}
        {showExplanation && (
          <Paper variant="outlined" className={classes.infoBox} elevation={0}>
            <Typography variant="body2">{infoText}</Typography>
          </Paper>
        )}
      </div>
    </Paper>
  );

  if (onOpen) {
    return (
      <button type="button" onClick={onOpen} className={classes.cardButton}>
        {paper}
      </button>
    );
  }

  return paper;
};

// --- Row styling ---

const useRowStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(1.5, 2) },
  thumbnail: {
    width: 120,
    height: 90,
    objectFit: 'cover',
    display: 'block',
    borderRadius: theme.shape.borderRadius,
    flexShrink: 0,
  },
  thumbnailPlaceholder: {
    width: 120,
    height: 90,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  model: { fontWeight: 600 },
  price: { fontWeight: 600, whiteSpace: 'nowrap' as const },
  clickable: { cursor: 'pointer' },
  rightActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  detailLink: {
    padding: 4,
    marginBottom: theme.spacing(0.25),
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.palette.grey[600],
    fontSize: '0.8125rem',
    border: 'none',
    background: 'none',
    padding: 0,
    marginTop: theme.spacing(0.5),
  },
  explanationBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  explanationList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    '& li': {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(0.75),
      '&:last-child': { marginBottom: 0 },
      '&::before': {
        content: '"▶"',
        color: theme.palette.intelligence.main,
        fontSize: '0.6rem',
        marginRight: theme.spacing(1),
        marginTop: 3,
        flexShrink: 0,
      },
    },
  },
}));

const RowStyle = ({
  imageThumbnailLink,
  altText,
  model,
  formattedPrice,
  actionLabel,
  infoText,
  detailHref,
  onOpen,
  reason,
}: SharedData & Pick<RecommendationCardProps, 'imageThumbnailLink' | 'model' | 'reason'>) => {
  const classes = useRowStyles();
  const shared = useSharedStyles();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const explanationLines = infoText ? infoText.split('\n').filter(Boolean) : [];

  return (
    <Paper className={classes.root} elevation={1}>
      <Grid container alignItems="center" spacing={2} wrap="nowrap">
        <Grid item onClick={onOpen} className={onOpen ? classes.clickable : undefined}>
          {imageThumbnailLink ? (
            <img src={imageThumbnailLink} alt={altText} className={classes.thumbnail} />
          ) : (
            <div className={classes.thumbnailPlaceholder}>
              <CameraAlt color="disabled" />
            </div>
          )}
        </Grid>
        <Grid item xs onClick={onOpen} className={onOpen ? classes.clickable : undefined}>
          {model && (
            <Typography variant="body1" className={classes.model}>
              {model}
            </Typography>
          )}
          {actionLabel && <span className={shared.actionBadge}>{actionLabel}</span>}
        </Grid>
        <Grid item className={classes.rightActions}>
          {detailHref && (
            <ActionTooltip title={t('navigation.detailPage')} position="left">
              <IconButton
                size="small"
                href={detailHref}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.detailLink}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </ActionTooltip>
          )}
          {formattedPrice && (
            <Typography variant="body1" className={classes.price}>
              {formattedPrice}
            </Typography>
          )}
          {reason && (
            <button type="button" className={classes.expandButton} onClick={() => setExpanded((v) => !v)}>
              {expanded ? t('recommendationsPage.card.hideExplanation') : t('recommendationsPage.card.showExplanation')}
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </button>
          )}
        </Grid>
      </Grid>
      <Collapse in={expanded}>
        <div className={classes.explanationBox}>
          <ul className={classes.explanationList}>
            {explanationLines.map((line) => (
              <li key={line}>
                <Typography variant="body2">{line}</Typography>
              </li>
            ))}
          </ul>
        </div>
      </Collapse>
    </Paper>
  );
};

// --- Public component ---

const RecommendationCard = ({
  variant = 'card',
  imageThumbnailLink,
  model,
  basePrice,
  recommendedAction,
  reason,
  source,
  carId,
  onOpen,
  showExplanation = false,
}: RecommendationCardProps) => {
  const { t } = useTranslation();
  const { format, convert } = useCurrency();

  const actionLabelMap: Partial<Record<RecommendedAction, string>> = {
    [RecommendedAction.Buy]: t('recommendationsPage.card.actions.buy'),
    [RecommendedAction.LowSupply]: t('recommendationsPage.card.actions.lowSupply'),
  };

  const shared: SharedData = {
    actionLabel: (recommendedAction && actionLabelMap[recommendedAction]) ?? undefined,
    formattedPrice: basePrice?.amount != null ? format(convert(basePrice, {}), {}) : undefined,
    infoText: getRecommendationReasonText(reason) ?? t('recommendationsPage.card.infoText'),
    altText: imageThumbnailLink ? (model ?? '') : t('recommendationsPage.card.noThumbnail'),
    detailHref: source && carId ? `/cars/${source}/${carId}` : undefined,
    onOpen,
  };

  if (variant === 'row') {
    return <RowStyle {...shared} imageThumbnailLink={imageThumbnailLink} model={model} reason={reason} />;
  }

  return (
    <CardStyle {...shared} imageThumbnailLink={imageThumbnailLink} model={model} showExplanation={showExplanation} />
  );
};

export default RecommendationCard;
